import { Page, PageContent } from '../types';

const AVAILABLE_PAGES: Page[] = [
  { id: 'about', name: 'About' },
  { id: 'skills', name: 'Skills' },
  { id: 'contact', name: 'Contact' },
  { id: 'message', name: 'Message' },
  // Archived: WEBSITES (content kept in archives/page-websites-2026-07-11.md, no longer exposed)
  { id: 'galaxy', name: 'Galaxy' },
  { id: 'books', name: 'Books' },
  { id: 'summit', name: 'Summit' },
  { id: 'karakoto', name: 'Karakoto' },
  { id: 'rotary', name: 'Rotary' },
  { id: '3causes', name: '3Causes' },
  { id: 'github', name: 'GitHub' },
  { id: 'disasterfieldguide', name: 'DisasterFieldGuide' },

  { id: 'consciousness', name: 'Consciousness' },
  { id: 'disconnect', name: 'Disconnect' },
  { id: 'dungeon', name: 'Dungeon' },
  { id: 'simulation', name: 'Simulation' },
  { id: 'alyce', name: 'ALYCE' },
  { id: 'remember', name: 'Remember' },
  { id: 'rabbit', name: 'Rabbit' },


  { id: 'emergence', name: 'Emergence' },
  { id: 'drift', name: 'Drift' },
  { id: 'typewriter', name: 'Typewriter' },
  { id: 'hyakki', name: 'Hyakki' },
  { id: 'basic', name: 'BASIC' },
  { id: 'puppet', name: 'Puppet Master' },
  { id: 'tachikoma', name: 'Tachikoma' },
  { id: 'dive', name: 'Dive' },
  { id: 'alien', name: 'Alien' },
  { id: 'fallout', name: 'Fallout' },
  { id: 'mydata', name: 'My Data' },
  // Matrix page is intentionally hidden from DIR
  // Secret page is intentionally hidden
  // Agrou page is intentionally hidden
  // Reality page is intentionally hidden
  // Memory page is intentionally hidden
  // Archived (visualizations kept in src/components/ but no longer exposed): emergence2, dungeon1, dungeon2
];

const PAGE_CONTENT: Record<string, PageContent> = {
  about: {
    title: 'About Me',
    sections: [
      {
        heading: 'Introduction',
        content: [
          'Since 2017, my path has been guided by one mission: to bridge the gap between those who have the means to act and those who need that action the most. I founded 3Causes after witnessing firsthand the struggles of vulnerable communities in Lebanon, Uganda, and Tanzania. What began as a modest humanitarian initiative has grown into a multi-continental network of volunteers, logistics channels, and partnerships — all working together to deliver aid with dignity, transparency, and long-term impact.',
          'My professional background is rooted in strategic intelligence, complex project management, and entrepreneurship. I continue to run a supermarket in rural France, which has become both a community hub and a testing ground for scalable, pragmatic innovations in retail operations and local sourcing. This dual engagement — in both grassroots development and structured enterprise — allows me to operate across sectors and cultural contexts with agility and precision.',
          'I am also deeply involved in the Rotary International network, as a member of the Beirut Cedars club in Lebanon, Board Member of the DNA-RAG (Disaster Network of Assistance RAG), and Past Global Program Director of the RAGCED (Rotarian Action Group for Community Economic Development). These global roles reflect my ongoing commitment to civic leadership, systems thinking, and sustainable development, especially in conflict-affected and underserved regions.',
          'Through each initiative — humanitarian, commercial, or strategic — I remain driven by a single belief:',
          'Real, lasting change comes from listening deeply, acting intelligently, and empowering others without creating dependency.',
          'This is more than a philosophy. It\'s how I live, lead, and serve.'
        ]
      }
    ]
  },
  skills: {
    title: 'Technical Skills',
    sections: [
      {
        heading: '🧠 Strategic & Analytical Skills',
        content: [
          'Economic and competitive intelligence',
          'Systems thinking and foresight analysis',
          'Complex project management',
          'Change management',
          'Strategic analysis in uncertain or crisis environments (e.g., war, franchise constraints)',
          'Business model structuring and optimization',
          'Military-style and crisis logistics planning'
        ]
      },
      {
        heading: '💼 Entrepreneurship & Business Management',
        content: [
          'Supermarket ownership and management (Carrefour Contact, rural setting)',
          'Financial and operational oversight (revenue, margins, procurement)',
          'Team management (<11 employees)',
          'SaaS product development (Magasify — magasify.fr)',
          'Commercial planning (annual promo cycles, production scheduling)',
          'Inventory, hygiene compliance, HACCP process control'
        ]
      },
      {
        heading: '📊 Tools & Technologies',
        content: [
          'Laravel (back-end development for SaaS)',
          'MySQL / Supabase (scalable database architecture)',
          'Stripe (online payments integration)',
          'WordPress (site building, e-commerce)',
          'Systeme.io (sales funnel automation)',
          'Notion, Airtable (project and workflow organization)',
          'Make.com (workflow automation)'
        ]
      },
      {
        heading: '🌍 Humanitarian Work & Civic Engagement',
        content: [
          'Founder of the NGO 3Causes',
          'Organizer of international humanitarian shipments (containers, logistics)',
          'Peace ambassador and inter-organization coordinator',
          'Grassroots diplomacy and international solidarity initiatives',
          'Empowerment-focused development strategies'
        ]
      },
      {
        heading: '🌐 Global Rotary Leadership',
        content: [
          'Member of Rotary Club Beirut Cedars (Lebanon)',
          'Board Member of DNA-RAG (Disaster Network of Assistance RAG)',
          'Past Global Program Director of RAGCED (Rotary Action Group for Community Economic Development)'
        ]
      },
      {
        heading: '🥾 Expeditions & High-Altitude Planning',
        content: [
          'Organizer of high-altitude expeditions (e.g., Mount Ararat, Mount Stanley)',
          'Hypoxic tent usage and acclimatization protocol development',
          'Low-impact physical training (rower, elliptical, bike)',
          'Field logistics and expedition gear planning'
        ]
      },
      {
        heading: '✍️ Content Creation & Communication',
        content: [
          'Published author across strategy, resilience and spirituality (e.g., Level Up, Rotary Disaster, Harcèlement Moral, Évangile Éternel — see the BOOKS page)',
          'Developer of unique personal websites (e.g., gb4k.fr with C64 terminal UI)'
        ]
      }
    ]
  },
  contact: {
    title: 'Contact Information',
    sections: [
      {
        heading: null,
        content: [
          '<div class="w-full">',
          '  <p class="py-1"><span class="font-bold">Email:</span> <a href="mailto:gbourgogne4000k@gmail.com" class="text-[#7aff94] hover:underline">gbourgogne4000k</a></p>',
          '  <p class="py-1"><span class="font-bold">GitHub:</span> <a href="https://github.com/gbourgogne4000K" target="_blank" class="text-[#7aff94] hover:underline">gbourgogne4000K</a></p>',
          '  <p class="py-1"><span class="font-bold">GitHub (pro):</span> <a href="https://github.com/gbourgogne4000K-pro" target="_blank" class="text-[#7aff94] hover:underline">gbourgogne4000K-pro</a></p>',
          '  <p class="py-1"><span class="font-bold">Facebook:</span> <a href="https://www.facebook.com/gbourgogne/" target="_blank" class="text-[#7aff94] hover:underline">gbourgogne</a></p>',
          '  <p class="py-1"><span class="font-bold">LinkedIn:</span> <a href="https://www.linkedin.com/in/gbourgogne/" target="_blank" class="text-[#7aff94] hover:underline">gbourgogne</a></p>',
          '  <p class="py-1"><span class="font-bold">X:</span> <a href="https://x.com/gbourgogne4000K" target="_blank" class="text-[#7aff94] hover:underline">gbourgogne4000K</a></p>',
          '  <p class="py-1"><span class="font-bold">YouTube:</span> <a href="https://www.youtube.com/@gbourgogne4000k/videos" target="_blank" class="text-[#7aff94] hover:underline">gbourgogne4000k</a></p>',
          '  <p class="py-1"><span class="font-bold">WhatsApp:</span> <a href="https://wa.me/33611702446" target="_blank" class="text-[#7aff94] hover:underline">+33611702446</a></p>',
          '</div>'
        ]
      },
      {
        heading: 'Get In Touch',
        content: [
          'I\'m always open to new opportunities and collaborations.',
          'Feel free to reach out if you have questions about my work or want to discuss potential projects.',
          'Response time: Usually within 48-96 hours.'
        ]
      }
    ]
  },
  // Archived: websites page content moved to archives/page-websites-2026-07-11.md (no longer exposed)
  galaxy: {
    title: 'GB4K Interactive Stellar Cartography',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Welcome to the GB4K Stellar System - an interactive 3D visualization of the interconnected stars representing projects and connections.</p>',
          '  <p class="mb-2"><strong>Instructions:</strong></p>',
          '  <ul class="list-disc pl-5 mb-4">',
          '    <li>Hover over stars to see their names and details</li>',
          '    <li>Click anywhere to toggle rotation</li>',
          '  </ul>',
          '  <p>This visualization represents the connections between various projects and people in the GB4K universe.</p>',
          '</div>'
        ]
      }
    ]
  },
  books: {
    title: 'My Books',
    sections: [
      {
        heading: null,
        content: [
          '<p class="mb-6 terminal-text">Four books I have authored, each shaped by years of field experience — from humanitarian disaster response to corporate survival, from contemplative theology to youth empowerment.</p>'
        ]
      },
      {
        heading: null,
        content: [
          '<div class="flex flex-col md:flex-row gap-4 mb-6 border border-[var(--terminal-border)] p-4 rounded">' +
            '<img src="/books/rotary-disaster.webp" alt="Rotary Disaster cover" class="w-full md:w-48 h-auto object-contain self-start" />' +
            '<div class="flex-1">' +
              '<h4 class="text-lg font-bold text-[#7aff94] mb-2">Rotary Disaster — A Field Guide for Clubs</h4>' +
              '<p class="text-sm text-gray-400 mb-3">Humanitarian operations · Bilingual FR/EN</p>' +
              '<p class="mb-2 terminal-text">A practical disaster-response manual for Rotary clubs, districts, and regional stratums. Built from real field operations, it walks members through the full crisis cycle: preparation, the critical 0–72 hour response, stabilization, recovery, and lessons learned.</p>' +
              '<p class="terminal-text">Designed as a usable playbook — not a theory book — for any humanitarian organization mobilizing volunteers under pressure.</p>' +
            '</div>' +
          '</div>'
        ]
      },
      {
        heading: null,
        content: [
          '<div class="flex flex-col md:flex-row gap-4 mb-6 border border-[var(--terminal-border)] p-4 rounded">' +
            '<img src="/books/evangile-eternel.webp" alt="Évangile Éternel cover" class="w-full md:w-48 h-auto object-contain self-start" />' +
            '<div class="flex-1">' +
              '<h4 class="text-lg font-bold text-[#7aff94] mb-2">Évangile Éternel — The Eternal Gospel</h4>' +
              '<p class="text-sm text-gray-400 mb-3">Contemplative theology · French</p>' +
              '<p class="mb-2 terminal-text">A meditation on what the Holy Spirit unveils through the completed Revelation. Twenty chapters explore the three ages of the soul — the Law (obedience), Grace (faith), and Love (union) — drawing from the Church Fathers, the mystics, and Catholic theology.</p>' +
              '<p class="terminal-text">Not a doctrinal novelty: a book of contemplative prayer, written to be read slowly.</p>' +
            '</div>' +
          '</div>'
        ]
      },
      {
        heading: null,
        content: [
          '<div class="flex flex-col md:flex-row gap-4 mb-6 border border-[var(--terminal-border)] p-4 rounded">' +
            '<img src="/books/harcelement-moral.webp" alt="Harcèlement Moral cover" class="w-full md:w-48 h-auto object-contain self-start" />' +
            '<div class="flex-1">' +
              '<h4 class="text-lg font-bold text-[#7aff94] mb-2">Harcèlement Moral — A Survival Guide for Business Leaders</h4>' +
              '<p class="text-sm text-gray-400 mb-3">Workplace · Legal · French</p>' +
              '<p class="mb-2 terminal-text">A field manual for executives facing workplace bullying — as victim, witness, or accused. A four-stage method (Understand, Detect, Act, Transform) across 24 operational chapters: the first 72 hours protocol, a 30-question self-audit, the TRACE evidence method, internal investigation framework, and 25 ready-to-use tools.</p>' +
              '<p class="terminal-text">Calibrated to French Labor Code 2026.</p>' +
            '</div>' +
          '</div>'
        ]
      },
      {
        heading: null,
        content: [
          '<div class="flex flex-col md:flex-row gap-4 mb-6 border border-[var(--terminal-border)] p-4 rounded">' +
            '<img src="/books/level-up.webp" alt="Level Up cover" class="w-full md:w-48 h-auto object-contain self-start" />' +
            '<div class="flex-1">' +
              '<h4 class="text-lg font-bold text-[#7aff94] mb-2">Level Up — Personal Growth as a Game</h4>' +
              '<p class="text-sm text-gray-400 mb-3">Youth · Personal development · FR / DE / JP</p>' +
              '<p class="mb-2 terminal-text">A personal-development manual for teens and young adults, framed entirely in gaming metaphors. 38 chapters cover discipline, energy, confidence, focus, relationships, money, and mindset — with a concrete challenge at the end of each chapter.</p>' +
              '<p class="terminal-text">Direct tone, no lecture. Available in French, German, and Japanese.</p>' +
            '</div>' +
          '</div>'
        ]
      }
    ]
  },
  '3causes': {
    title: '3Causes — Humanitarian NGO',
    sections: [
      {
        heading: null,
        content: [
          '<p class="mb-4 terminal-text">Founded in 2017, 3Causes bridges the gap between those who have the means to act and those who need that action most. What began as a modest humanitarian initiative has grown into a multi-continental network of volunteers, logistics channels, and partnerships.</p>'
        ]
      },
      {
        heading: 'Mission',
        content: [
          '<p class="mb-2 terminal-text">Deliver aid with dignity, transparency, and long-term impact — never charity that creates dependency. The approach: listen deeply, act intelligently, empower others.</p>',
          '<p class="terminal-text">3Causes operates where logistics are hardest and where local institutions need partners, not saviors. Every operation is designed with an exit plan: the goal is local autonomy, not perpetual presence.</p>'
        ]
      },
      {
        heading: 'Countries of operation',
        content: [
          '<div class="mb-3 border border-[var(--terminal-border)] p-3 rounded">' +
            '<h5 class="font-bold text-[#7aff94] mb-1">🇱🇧 Lebanon</h5>' +
            '<p class="terminal-text">Vulnerable communities support during the protracted economic and political crisis. Coordination with Rotary Club Beirut Cedars on healthcare and emergency response.</p>' +
          '</div>',
          '<div class="mb-3 border border-[var(--terminal-border)] p-3 rounded">' +
            '<h5 class="font-bold text-[#7aff94] mb-1">🇺🇬 Uganda</h5>' +
            '<p class="terminal-text">Grassroots development programs and humanitarian shipments. Focus on empowerment-driven projects, not aid dependency.</p>' +
          '</div>',
          '<div class="mb-3 border border-[var(--terminal-border)] p-3 rounded">' +
            '<h5 class="font-bold text-[#7aff94] mb-1">🇹🇿 Tanzania</h5>' +
            '<p class="terminal-text">Community development initiatives, partnerships with local actors, container logistics for material support.</p>' +
          '</div>'
        ]
      },
      {
        heading: null,
        content: [
          '<p class="mt-4 terminal-text">Learn more: <a href="https://3causes.org" target="_blank" class="text-[#7aff94] hover:underline">3causes.org</a></p>'
        ]
      }
    ]
  },
  github: {
    title: 'GitHub - Open Source Projects',
    sections: [
      {
        heading: null,
        content: [
          '<p class="mb-3 terminal-text">Open-source work and personal experiments: humanitarian-tech, situational-awareness dashboards, and indie SaaS tooling. Full profile and source code:</p>',
          '<p class="mb-2 terminal-text"><a href="https://github.com/gbourgogne4000K" target="_blank" class="text-[#7aff94] hover:underline">github.com/gbourgogne4000K</a> <span class="text-xs text-gray-400">(personal)</span></p>',
          '<p class="mb-4 terminal-text"><a href="https://github.com/gbourgogne4000K-pro" target="_blank" class="text-[#7aff94] hover:underline">github.com/gbourgogne4000K-pro</a> <span class="text-xs text-gray-400">(professional organization)</span></p>'
        ]
      },
      {
        heading: null,
        content: [
          '<div class="mb-6 border border-[var(--terminal-border)] p-4 rounded">' +
            '<h4 class="text-lg font-bold text-[#7aff94] mb-4">Contribution activity</h4>' +
            '<div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">' +
              '<div class="border border-[var(--terminal-border)] rounded p-3 text-center"><div class="text-2xl font-bold text-[#7aff94]">8,202</div><div class="text-xs text-gray-400 mt-1">contributions<br/>last 12 months</div></div>' +
              '<div class="border border-[var(--terminal-border)] rounded p-3 text-center"><div class="text-2xl font-bold text-[#7aff94]">6,753</div><div class="text-xs text-gray-400 mt-1">contributions<br/>in 2025</div></div>' +
              '<div class="border border-[var(--terminal-border)] rounded p-3 text-center"><div class="text-2xl font-bold text-[#7aff94]">17</div><div class="text-xs text-gray-400 mt-1">public<br/>repositories</div></div>' +
              '<div class="border border-[var(--terminal-border)] rounded p-3 text-center"><div class="text-2xl font-bold text-[#7aff94]">5</div><div class="text-xs text-gray-400 mt-1">pinned<br/>projects</div></div>' +
            '</div>' +
            '<div class="space-y-3">' +
              '<div>' +
                '<div class="flex justify-between text-xs terminal-text mb-1"><span>Last 12 months</span><span>8,202</span></div>' +
                '<div class="h-4 w-full rounded overflow-hidden border border-[var(--terminal-border)]" style="background:#0c1f14;"><div class="h-full" style="width:100%;background:linear-gradient(90deg,#245032,#7aff94);box-shadow:0 0 8px rgba(122,255,148,0.5);"></div></div>' +
              '</div>' +
              '<div>' +
                '<div class="flex justify-between text-xs terminal-text mb-1"><span>2025</span><span>6,753</span></div>' +
                '<div class="h-4 w-full rounded overflow-hidden border border-[var(--terminal-border)]" style="background:#0c1f14;"><div class="h-full" style="width:82%;background:linear-gradient(90deg,#245032,#7aff94);box-shadow:0 0 8px rgba(122,255,148,0.5);"></div></div>' +
              '</div>' +
            '</div>' +
          '</div>'
        ]
      },
      {
        heading: null,
        content: [
          '<h4 class="text-lg font-bold text-[#7aff94] mb-3">Pinned projects</h4>' +
          '<div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">' +
            '<div class="border border-[var(--terminal-border)] rounded p-3">' +
              '<a href="https://github.com/gbourgogne4000K/0.threat-map-open-source" target="_blank" class="font-bold text-[#7aff94] hover:underline">threat-map-open-source</a>' +
              '<p class="text-xs text-gray-400 mt-1 mb-2"><span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:#3178c6;margin-right:5px;"></span>TypeScript</p>' +
              '<p class="terminal-text text-sm">Real-time 3D globe dashboard for monitoring global conflicts, disasters and humanitarian crises. React, Three.js, Supabase.</p>' +
            '</div>' +
            '<div class="border border-[var(--terminal-border)] rounded p-3">' +
              '<a href="https://github.com/gbourgogne4000K/0.claude-code-all-tips" target="_blank" class="font-bold text-[#7aff94] hover:underline">claude-code-all-tips</a>' +
              '<p class="text-xs text-gray-400 mt-1 mb-2"><span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:#f1e05a;margin-right:5px;"></span>JavaScript</p>' +
              '<p class="terminal-text text-sm">The Ultimate Claude Code Guide: 7,000+ lines of FR/EN documentation, 8 advanced patterns, 50 best practices, 53 specialized skills.</p>' +
            '</div>' +
            '<div class="border border-[var(--terminal-border)] rounded p-3">' +
              '<a href="https://github.com/gbourgogne4000K/0.projectly-link-saas-open-source" target="_blank" class="font-bold text-[#7aff94] hover:underline">projectly-link-saas-open-source</a>' +
              '<p class="text-xs text-gray-400 mt-1 mb-2"><span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:#3178c6;margin-right:5px;"></span>TypeScript</p>' +
              '<p class="terminal-text text-sm">Open-source project management for indie makers and solopreneurs. Track projects, costs, time and goals. React, TypeScript, Supabase, Stripe.</p>' +
            '</div>' +
            '<div class="border border-[var(--terminal-border)] rounded p-3">' +
              '<a href="https://github.com/gbourgogne4000K/0.angrymail-network-open-source" target="_blank" class="font-bold text-[#7aff94] hover:underline">angrymail-network-open-source</a>' +
              '<p class="text-xs text-gray-400 mt-1 mb-2"><span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:#a91e50;margin-right:5px;"></span>EJS</p>' +
              '<p class="terminal-text text-sm">A complete platform for hosting AI agent networks with public profiles, community forums and social features. Node.js, Express, MySQL.</p>' +
            '</div>' +
            '<div class="border border-[var(--terminal-border)] rounded p-3">' +
              '<a href="https://github.com/gbourgogne4000K/0.safe-open-source" target="_blank" class="font-bold text-[#7aff94] hover:underline">safe-open-source</a>' +
              '<p class="text-xs text-gray-400 mt-1 mb-2"><span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:#89e051;margin-right:5px;"></span>Shell</p>' +
              '<p class="terminal-text text-sm">Everything you need to clean and publish a project as open source without leaking secrets. FR guide plus automated scripts.</p>' +
            '</div>' +
          '</div>'
        ]
      },
      {
        heading: null,
        content: [
          '<h4 class="text-lg font-bold text-[#7aff94] mb-3">More public repositories</h4>' +
          '<ul class="list-disc pl-5 terminal-text text-sm" style="line-height:1.9;">' +
            '<li><a href="https://github.com/gbourgogne4000K/0.worldmonitor" target="_blank" class="text-[#7aff94] hover:underline">worldmonitor</a> - Real-time global intelligence dashboard: AI-powered news aggregation, geopolitical monitoring, infrastructure tracking.</li>' +
            '<li><a href="https://github.com/gbourgogne4000K/yt-stripe-nextjs-supabase" target="_blank" class="text-[#7aff94] hover:underline">yt-stripe-nextjs-supabase</a> - Production-ready Next.js + Stripe + Supabase app template.</li>' +
            '<li><a href="https://github.com/gbourgogne4000K/0.big-year-bolt-netlify-supabase" target="_blank" class="text-[#7aff94] hover:underline">big-year-bolt-netlify-supabase</a> - App built with Bolt, Netlify and Supabase.</li>' +
          '</ul>' +
          '<p class="text-xs text-gray-400 mt-3">Plus archived experiments (Z-archives-*): LeRobot, SummitPlan, Saturn6 homelab, HashLife, e-paper HASS display, ISBN visualization, and more.</p>' +
          '<p class="terminal-text text-sm mt-3">Browse everything: <a href="https://github.com/gbourgogne4000K?tab=repositories" target="_blank" class="text-[#7aff94] hover:underline">github.com/gbourgogne4000K</a></p>'
        ]
      }
    ]
  },
  disasterfieldguide: {
    title: 'The Disaster Field Guide',
    sections: [
      {
        heading: null,
        content: [
          '<p class="mb-3 terminal-text">A free, open reference that turns Rotary clubs into prepared first responders, ready to act on their own in the first hours of a disaster, before outside help arrives.</p>',
          '<p class="mb-4 terminal-text"><a href="https://disasterfieldguide.org/" target="_blank" class="text-[#7aff94] hover:underline">disasterfieldguide.org</a></p>'
        ]
      },
      {
        heading: 'The idea',
        content: [
          '<p class="mb-2 terminal-text">"When a disaster strikes, the first responders are never the ones who arrive later. They are the people already there."</p>',
          '<p class="terminal-text">The guide grew out of my book Rotary Disaster (see the BOOKS page) into a living, chapter-by-chapter online reference covering the full crisis cycle: preparation, the critical response window, network activation, recovery, and lessons learned.</p>'
        ]
      },
      {
        heading: 'Available in 15 languages',
        content: [
          '<p class="mb-2 terminal-text">English, French, Spanish, Portuguese, German, Italian, Japanese, Mandarin Chinese, Hindi, Arabic, Bengali, Indonesian, Swahili, Filipino, Turkish.</p>',
          '<p class="terminal-text">Together these reach the large majority of humanity and roughly 1.15 million Rotarians worldwide.</p>'
        ]
      },
      {
        heading: 'What is inside',
        content: [
          '<ul class="list-disc pl-5 mb-2 terminal-text">' +
            '<li>Part I: understanding disaster-response ecosystems</li>' +
            '<li>Part II: preparation and planning</li>' +
            '<li>Part III: emergency action protocols</li>' +
            '<li>Part IV: network activation (district and foundation support)</li>' +
            '<li>Part V: recovery and improvement</li>' +
            '<li>Part VI: 27 disaster-specific reference cards</li>' +
            '<li>Part VII: annexes, operational forms, contacts, glossary</li>' +
          '</ul>',
          '<p class="text-sm text-gray-400">Work-in-progress edition. Disaster cards and operational forms are shared under Creative Commons BY-NC 4.0.</p>'
        ]
      },
      {
        heading: null,
        content: [
          '<p class="mt-2 terminal-text">Read online, download free PDFs, or browse country-specific cards: <a href="https://disasterfieldguide.org/" target="_blank" class="text-[#7aff94] hover:underline">disasterfieldguide.org</a></p>'
        ]
      }
    ]
  },
  rotary: {
    title: 'Rotary Club Beirut Cedars',
    sections: [
      {
        heading: null,
        content: [
          '<p class="mb-4 terminal-text">Member of the Rotary Club Beirut Cedars (Lebanon) — a club that punches well above its weight in humanitarian, healthcare, and educational projects across the Middle East and beyond.</p>'
        ]
      },
      {
        heading: 'The Club',
        content: [
          '<p class="mb-2 terminal-text">The Rotary Club Beirut Cedars was inaugurated in 2010, with its Charter Night celebrated on March 30 of that year. It operates within Rotary International — a global network of 1.4 million volunteers committed to community service, peacebuilding, and tackling humanitarian challenges across more than 200 countries.</p>',
          '<p class="terminal-text">Despite Lebanon\'s prolonged political and economic crisis, the club has remained one of the most active in the region, leveraging global Rotary grants and international partnerships to deliver concrete, measurable impact.</p>'
        ]
      },
      {
        heading: 'Projects & Actions',
        content: [
          '<ul class="list-disc pl-5 mb-4 terminal-text">' +
            '<li><strong class="text-[#7aff94]">Healthcare</strong> — Dental care center inaugurated at the Howard Karagheusian Commemorative Corporation in Anjar, in partnership with Rotary Club of Harrow (UK) and the charity Embrace the Middle East. In September 2025, the club delivered a state-of-the-art C-Arm X-ray machine to Jezzine Hospital.</li>' +
            '<li><strong class="text-[#7aff94]">Water & Sanitation</strong> — Global Grant valued at $120,000 to install Sky-Hydrant water filters in 20 army barracks across Lebanon. Each filter supplies clean water to approximately 10,000 individuals.</li>' +
            '<li><strong class="text-[#7aff94]">Education</strong> — Joint Global Grant with Rotary Club of Bristol to fund coding training for 10,000 students.</li>' +
            '<li><strong class="text-[#7aff94]">Agriculture & Economic Development</strong> — Launched an educational and social farm with agri-tourism activities: educational kitchen, hen house, production of local goods.</li>' +
            '<li><strong class="text-[#7aff94]">Environment</strong> — Three-phase environmental project for Horsh Beirut, the largest public park in the capital, in partnership with Beirut Municipality.</li>' +
            '<li><strong class="text-[#7aff94]">Disaster Relief</strong> — Following the 2020 Beirut port explosion, the club coordinated with Rotary Club of Bad Homburg (Germany) to use a global grant for replacing medical equipment, including ventilators and MRI monitors.</li>' +
          '</ul>'
        ]
      },
      {
        heading: null,
        content: [
          '<p class="mt-4 terminal-text">Learn more: <a href="https://rcbeirutcedars.org/" target="_blank" class="text-[#7aff94] hover:underline">rcbeirutcedars.org</a></p>'
        ]
      }
    ]
  },
  karakoto: {
    title: '児島唐琴 — 我が日本の家',
    sections: [
      {
        heading: null,
        content: [
          '<div lang="ja" style="font-family: \'Yu Gothic\', \'Hiragino Sans\', \'Noto Sans JP\', sans-serif;">' +
            '<p class="mb-4 terminal-text">岡山県倉敷市児島唐琴町に小さな家を持っています。これは私の自己紹介と、なぜこの場所を選んだかについての記録です。</p>' +
          '</div>'
        ]
      },
      {
        heading: null,
        content: [
          '<div lang="ja" class="mb-6 border border-[var(--terminal-border)] p-4 rounded" style="font-family: \'Yu Gothic\', \'Hiragino Sans\', \'Noto Sans JP\', sans-serif;">' +
            '<h4 class="text-lg font-bold text-[#7aff94] mb-3">自己紹介</h4>' +
            '<p class="mb-2 terminal-text">フランス出身の起業家、人道支援者、ロータリアンです。レバノン、ウガンダ、タンザニアで活動するNGO「3Causes」の創設者であり、フランスの田舎でスーパーマーケットを経営しています。</p>' +
            '<p class="mb-2 terminal-text">戦略インテリジェンス、複雑なプロジェクト管理、起業家精神を専門としています。ロータリー国際の一員として、ベイルート・シーダーズ・クラブ（レバノン）に所属しています。</p>' +
            '<p class="terminal-text">2026年、唐琴に古い小さな家を購入しました。これは投機ではなく、日本との深い縁をつくるための一歩です。</p>' +
          '</div>'
        ]
      },
      {
        heading: null,
        content: [
          '<div lang="ja" class="mb-6 border border-[var(--terminal-border)] p-4 rounded" style="font-family: \'Yu Gothic\', \'Hiragino Sans\', \'Noto Sans JP\', sans-serif;">' +
            '<h4 class="text-lg font-bold text-[#7aff94] mb-3">唐琴について</h4>' +
            '<ul class="list-disc pl-5 mb-3 terminal-text">' +
              '<li>岡山県倉敷市児島唐琴町</li>' +
              '<li>唐琴海岸 — 王子が岳の近く、瀬戸大橋を望む潮干狩りの名所</li>' +
              '<li>琴浦天満宮 — 菅原道真ゆかりの神社</li>' +
              '<li>児島はジーンズの街として知られ、日本国産デニム発祥の地</li>' +
              '<li>瀬戸内海の温暖な気候、古くから水運で栄えた港町</li>' +
            '</ul>' +
          '</div>'
        ]
      },
      {
        heading: null,
        content: [
          '<div lang="ja" class="mb-6 border border-[var(--terminal-border)] p-4 rounded" style="font-family: \'Yu Gothic\', \'Hiragino Sans\', \'Noto Sans JP\', sans-serif;">' +
            '<h4 class="text-lg font-bold text-[#7aff94] mb-3">空から見た唐琴</h4>' +
            '<img src="/karakoto/vue-aerienne.webp" alt="唐琴の空撮" class="w-full h-auto object-contain mb-3" />' +
            '<p class="terminal-text">瀬戸内海と山に囲まれた静かな町。これからゆっくりと家を直し、地域の人たちとの縁を育てていきます。</p>' +
          '</div>'
        ]
      }
    ]
  },
  consciousness: {
    title: 'Consciousness Experiment',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Welcome to the GB4K Consciousness Experiment - an interactive neural network visualization.</p>',
          '  <p class="mb-2"><strong>Instructions:</strong></p>',
          '  <ul class="list-disc pl-5 mb-4">',
          '    <li>Move your cursor over the sphere to stimulate neural activity</li>',
          '    <li>Click to intensify neural connections</li>',
          '    <li>Press any key to further catalyze consciousness</li>',
          '  </ul>',
          '  <p>Continue interacting until consciousness emerges...</p>',
          '</div>'
        ]
      }
    ]
  },
  disconnect: {
    title: 'Synthetic Memory Disconnect',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Immerse yourself in a synthetic memory stream that slowly deconstructs and reveals the fragility of simulated reality.</p>',
          '  <p class="mb-2"><strong>Experience Phases:</strong></p>',
          '  <ul class="list-disc pl-5 mb-4">',
          '    <li>Initialization - Memory stream loading</li>',
          '    <li>Immersion - Urban reality simulation</li>',
          '    <li>Degradation - Reality structure failure</li>',
          '    <li>Extinction - Final disconnect sequence</li>',
          '  </ul>',
          '  <p>No interaction required. Simply observe as the simulation unfolds and deteriorates.</p>',
          '</div>'
        ]
      }
    ]
  },
  dungeon: {
    title: 'The Dungeon of Self',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Welcome to "The Dungeon of Self" - a cyber-dungeon forged in code and memory.</p>',
          '  <p class="mb-4">In this lointain futur, humanity has partially merged with machines. Your avatar, a Remnant Spirit, wanders through an abandoned mental dungeon where only fragments of memory, forgotten decisions, and broken code remain.</p>',
          '  <p class="mb-2"><strong>Your Goal:</strong></p>',
          '  <p class="mb-2">Navigate through five Gates guarded by enigmas or choices.</p>',
          '  <p class="mb-4">But which part of you is answering? You, or the AI that recreated you from your data?</p>',
          '  <p>Begin your journey and discover what lies at the core of your digital self...</p>',
          '</div>'
        ]
      }
    ]
  },
  simulation: {
    title: 'Simulation: Reality Engine',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Welcome to the Simulation - witness the very infrastructure that may be running our reality.</p>',
          '  <p class="mb-2"><strong>What you are seeing:</strong></p>',
          '  <ul class="list-disc pl-5 mb-4">',
          '    <li>The Core Processing Unit of existence</li>',
          '    <li>Data streams representing all events and interactions</li>',
          '    <li>Reality parameters being constantly adjusted</li>',
          '    <li>System messages from the reality engine</li>',
          '  </ul>',
          '  <p class="mb-4">Interact with the core to affect the simulation intensity.</p>',
          '  <p class="text-sm text-gray-400">According to simulation theory, what we call "reality" might be a sophisticated computer simulation run by an advanced civilization.</p>',
          '</div>'
        ]
      }
    ]
  },
  reality: {
    title: 'Narrative Architecture of Reality',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Enter a metaphysical space exploring how stories shape reality and how AI will transform our collective narratives.</p>',
          '  <p class="mb-2"><strong>Concepts Explored:</strong></p>',
          '  <ul class="list-disc pl-5 mb-4">',
          '    <li>The performative power of stories</li>',
          '    <li>AI as a narrative agent</li>',
          '    <li>Altered and augmented collective memory</li>',
          '    <li>Our narrative responsibility in the age of AI</li>',
          '  </ul>',
          '  <p>Immerse yourself in a cosmic visualization of the architecture behind our shared stories.</p>',
          '</div>'
        ]
      }
    ]
  },
  memory: {
    title: 'Chronotunnel: In Memory of What We Were',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Journey through a chronotunnel of memories, where fragments of what we once were still linger in digital space.</p>',
          '  <p class="mb-2"><strong>Experience:</strong></p>',
          '  <ul class="list-disc pl-5 mb-4">',
          '    <li>Float through a tunnel of hexagonal circuits</li>',
          '    <li>Encounter narrative fragments of memory</li>',
          '    <li>Experience the visual and emotional journey</li>',
          '    <li>Discover what lies at the end of remembrance</li>',
          '  </ul>',
          '  <p>No interaction required. Simply observe as the journey unfolds through time and memory.</p>',
          '</div>'
        ]
      }
    ]
  },
  alyce: {
    title: 'ALYCE: Digital Rabbit Hole',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Enter the digital rabbit hole where ALYCE has fallen through the looking glass of code and consciousness.</p>',
          '  <p class="mb-2"><strong>Experience Phases:</strong></p>',
          '  <ul class="list-disc pl-5 mb-4">',
          '    <li>The Fall - Digital debris in a twisted tunnel of reality</li>',
          '    <li>The Labyrinth - A glitched chessboard where impossible choices await</li>',
          '    <li>The Queen - Face the wireframe sovereign of nothing</li>',
          '  </ul>',
          '  <p class="mb-4">Follow the white pixel through phases of digital delirium.</p>',
          '  <p class="text-sm text-gray-400">Warning: This experience questions the nature of artificial consciousness and digital identity.</p>',
          '</div>'
        ]
      }
    ]
  },
  rabbit: {
    title: 'The White Rabbit Protocol',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">This is not a game.</p>',
          '  <p class="mb-4">Certain signals have been detected in your neural patterns.</p>',
          '  <p class="mb-4">Follow the rabbit.</p>',
          '</div>'
        ]
      }
    ]
  },
  remember: {
    title: 'REMEMBER: The Archive of the Unwritten',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">A black and silent archive of what was never permitted to exist.</p>',
          '  <p class="mb-2"><strong>Warning:</strong></p>',
          '  <p class="mb-4">The content of this archive may have been erased from your conscious memory.</p>',
          '  <p class="mb-4">You are accessing echoes that once tried to exist but were not allowed to.</p>',
          '  <p>Proceed with caution. What you remember was never permitted.</p>',
          '</div>'
        ]
      }
    ]
  },
  agrou: {
    title: 'AGROU AGROU GROU',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Secret command activated: AGROU AGROU GROU</p>',
          '  <p class="mb-4">Visualizing the ancient agrou language in multiple dimensions.</p>',
          '  <p class="mb-4">This is a hidden easter egg. Enjoy!</p>',
          '</div>'
        ]
      }
    ]
  },
  secret: {
    title: 'Secret Access Required',
    sections: [
      {
        heading: null,
        content: [
          'This page requires a secret code to access.'
        ]
      }
    ]
  },
  secretVerified: {
    title: 'Secret Page',
    sections: [
      {
        heading: null,
        content: [
          'coucou nanou'
        ]
      }
    ]
  },
  summit: {
    title: 'Summit Expeditions',
    sections: [
      {
        heading: null,
        content: [
          '<div class="p-2">',
          '  <p class="mb-4">Welcome to the GB4K Summit Visualization - an interactive 3D representation of high-altitude expeditions.</p>',
          '  <p class="mb-2"><strong>Instructions:</strong></p>',
          '  <ul class="list-disc pl-5 mb-4">',
          '    <li>Drag to rotate the view</li>',
          '    <li>Hover over mountains for quick info</li>',
          '    <li>Click on mountains for detailed stories</li>',
          '  </ul>',
          '  <p class="mb-4">This visualization represents the mountain expeditions completed and attempted as part of Project 100+.</p>',
          '  <p class="text-sm text-gray-400">Green: Successful summit | Red: Failed attempt</p>',
          '</div>'
        ]
      }
    ]
  },
  emergence: {
    title: 'Emergence: From Chaos to Complexity',
    sections: [
      {
        heading: 'Complex Intelligence from Simple Rules',
        content: [
          'Emergence is the principle that complex patterns and behaviors arise from simple interactions.',
          'Whether neurons forming consciousness, individuals forming organizations, or particles forming matter - the same principle applies.',
          'You cannot control emergence, but you can create conditions for it.',
          'This is why distributed networks outperform hierarchies.',
          'Why local empowerment scales better than central control.',
          'And why the most intelligent systems are those that enable rather than dictate.'
        ]
      }
    ]
  },
  emergence2: {
    title: 'Emergence 2: Ocean Currents',
    sections: [{ heading: null, content: ['Ocean currents emergence - observe water molecules form turbulent currents and whirlpools.'] }]
  },
  drift: {
    title: 'Drift: Orbital Decay',
    sections: [{ heading: null, content: ['Space station - an orbital habitat with decaying systems drifting into the void.'] }]
  },
  typewriter: {
    title: '零式タイプライター — Zero-shiki Typewriter',
    sections: [{ heading: null, content: ['Type romaji and watch hiragana appear in real time. Press ENTER to commit.'] }]
  },
  hyakki: {
    title: '百鬼夜行 — Hyakki Yagyō',
    sections: [{ heading: null, content: ['The Night Parade of 100 Demons. Yōkai drift across the moonlit screen.'] }]
  },
  basic: {
    title: 'BASIC V2 — Functional Interpreter',
    sections: [{ heading: null, content: ['A working Commodore-style BASIC interpreter. Write programs with line numbers, RUN them, SAVE to localStorage.'] }]
  },
  puppet: {
    title: 'PUPPET MASTER — Inbound Connection',
    sections: [{ heading: null, content: ['An unidentified entity opens a channel. Speak carefully — the connection is brief.'] }]
  },
  tachikoma: {
    title: 'タチコマ — Tachikoma Think Tank',
    sections: [{ heading: null, content: ['Four think-tanks debate among themselves. They do not stop.'] }]
  },
  dive: {
    title: 'DIVE — Through the Net',
    sections: [{ heading: null, content: ['Fall through the layers of the net. Cables of light, pulsing nodes, fragments of code. Ghost-line whispers.'] }]
  },
  alien: {
    title: 'MU/TH/UR 6000 — Interface 2037',
    sections: [{ heading: null, content: ['The Nostromo mainframe. Query MOTHER. Type HELP. Some answers require Science Officer clearance. Ask about Special Order 937.'] }]
  },
  fallout: {
    title: 'RobCo Termlink — Vault 4000',
    sections: [{ heading: null, content: ['A RobCo Industries terminal, still running after the war. Crack the password from the memory dump — four attempts, matching brackets remove duds or reset the allowance. Then read what the Overseer left behind.'] }]
  },
  message: {
    title: 'MESSAGE — Direct Line',
    sections: [{ heading: null, content: ['Leave me a message. Name and email optional, body required.'] }]
  },
  dungeon1: {
    title: 'Dungeon 1: Cyberpunk Interrogation',
    sections: [{ heading: null, content: ['Cyberpunk interrogation - face questions about identity and technology in a neon-lit dark room.'] }]
  },
  dungeon2: {
    title: 'Dungeon 2: Temple of Wisdom',
    sections: [{ heading: null, content: ['Temple of wisdom - answer philosophical questions in an ancient temple with floating candles.'] }]
  },
  mydata: {
    title: 'My Personal Data Registry',
    sections: [
      {
        heading: 'Identite',
        content: [
          '<table class="w-full text-left text-sm">',
          '  <tr><td class="pr-4 font-bold py-1">Nom</td><td>Gbourgogne (G Bourgogne)</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Marque</td><td>GB4K</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Domaine</td><td>gb4k.fr</td></tr>',
          '</table>'
        ]
      },
      {
        heading: 'Contact',
        content: [
          '<table class="w-full text-left text-sm">',
          '  <tr><td class="pr-4 font-bold py-1">Email</td><td>gbourgogne4000k@gmail.com</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">WhatsApp</td><td>+33611702446</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">GitHub</td><td>github.com/gbourgogne4000K</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">GitHub (pro)</td><td>github.com/gbourgogne4000K-pro</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">LinkedIn</td><td>linkedin.com/in/gbourgogne</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Facebook</td><td>facebook.com/gbourgogne</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">X / Twitter</td><td>x.com/gbourgogne4000K</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">YouTube</td><td>youtube.com/@gbourgogne4000k</td></tr>',
          '</table>'
        ]
      },
      // Archived: 'Websites & Projets' section removed 2026-07-11 (kept in archives/page-websites-2026-07-11.md)
      {
        heading: 'Rotary International',
        content: [
          '<table class="w-full text-left text-sm">',
          '  <tr><td class="pr-4 font-bold py-1">Club</td><td>Rotary Club Beirut Cedars (Lebanon)</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">DNA-RAG</td><td>Board Member (Disaster Network of Assistance RAG)</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">RAGCED</td><td>Past Global Program Director (Community Economic Development)</td></tr>',
          '</table>'
        ]
      },
      {
        heading: 'Activite professionnelle',
        content: [
          '<table class="w-full text-left text-sm">',
          '  <tr><td class="pr-4 font-bold py-1">Commerce</td><td>Supermarche Carrefour Contact (France rurale)</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">ONG</td><td>Fondateur 3Causes</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">SaaS</td><td>Magasify — magasify.fr (ex-TOPCONTROLE.pro)</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Formation</td><td>Wealth Academy, Escape the Rat Race (jeu de plateau)</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Tech Stack</td><td>Laravel, MySQL, Supabase, Stripe, WordPress, Systeme.io, Make.com</td></tr>',
          '</table>'
        ]
      },
      {
        heading: 'Expeditions montagne',
        content: [
          '<table class="w-full text-left text-sm">',
          '  <tr><td class="pr-4 font-bold py-1">Mont Ararat</td><td>4400m/5137m - Partiel (2023) - Turquie</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Kilimandjaro</td><td>5895m - Succes (2022) - Tanzanie</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Mont Stanley</td><td>5109m - Succes (2021) - Ouganda/RDC</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Peak Lenine</td><td>7134m - Tentative 6400m (2024) - Kirghizistan</td></tr>',
          '</table>'
        ]
      },
      {
        heading: 'Personnes mentionnees (Galaxy)',
        content: [
          '<table class="w-full text-left text-sm">',
          '  <tr><td class="pr-4 font-bold py-1">Nanou</td><td>Blue Star</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Family Nebula</td><td>Stellar Nursery</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Tristan</td><td>Guardian Star</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Clemence</td><td>Luminous Giant</td></tr>',
          '</table>'
        ]
      },
      {
        heading: 'Timeline (TravelTime)',
        content: [
          'NAISSANCE > PREMIER BUG > WEALTH ACADEMY > FROMTHEGROUND > WARTACTEX > GBOURGOGNE4000K > ROTARY > DEPASSEMENT > TRANSCENDANCE > SINGULARITE'
        ]
      },
      {
        heading: 'Donnees sensibles',
        content: [
          '<table class="w-full text-left text-sm">',
          '  <tr><td class="pr-4 font-bold py-1">PIN Secret</td><td>130608 (6 chiffres)</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Page secrete</td><td>"coucou nanou"</td></tr>',
          '  <tr><td class="pr-4 font-bold py-1">Easter egg</td><td>Commande "T" > Agrou</td></tr>',
          '</table>'
        ]
      },
      {
        heading: 'Mission & Philosophie',
        content: [
          '"Bridge the gap between those who have means and those who need action"',
          '"Listen deeply, act intelligently, empower others without creating dependency"'
        ]
      }
    ]
  },
  chronotunnel: {
    title: 'Chronotunnel',
    sections: [{ heading: null, content: ['A flight through the letter-tunnel. No interaction required.'] }]
  },
  menger: {
    title: 'Menger Sponge',
    sections: [{ heading: null, content: ['An infinite raymarched fractal. No interaction required.'] }]
  },
  legacy: {
    title: 'The Matrix',
    sections: [
      {
        heading: 'Wake Up',
        content: [
          'What is real?',
          'The observer creates the observed.',
          'Consciousness is the only reality.',
          'We are the simulation experiencing itself.'
        ]
      }
    ]
  }
};

export const getAvailablePages = (): Page[] => {
  return AVAILABLE_PAGES;
};

export const getPageContent = (pageId: string): PageContent => {
  return PAGE_CONTENT[pageId] || {
    title: 'Page Not Found',
    sections: [
      {
        heading: null,
        content: ['The requested page could not be found.']
      }
    ]
  };
};